import React, { useEffect, useState } from "react";
import { Alert, Button, Empty, Spin, Tag } from "antd";
import moment from "moment-jalaali";
import { useReportsStore } from "../../../store/slices/createReportsSlice";
import strings from "../../../utils/strings";

/**
 * نمایش اطلاعات پروفایل کاربر بر اساس سرویس /v1/crm/user-info/{userId}
 * کامپوننت مشترک بین «جستجوی بازاریاب» و «جست و جوی کاربر».
 */

const FIELD_LABELS = {
    Name: "نام",
    LastName: "نام خانوادگی",
    FatherName: "نام پدر",
    NationalCode: "کد ملی",
    BirthDate: "تاریخ تولد",
    Job: "شغل",

    MobileNumber: "شماره موبایل",
    Phone: "تلفن",
    Address: "آدرس",
    City: "استان",
    Town: "شهر",

    BussinessName: "نام بیزینس",
    UserName: "نام کاربری",
    GroupNames: "گروه کاربری",
    Roles: "نقش‌ها",
    UserId: "شناسه کاربر",
    Balance: "موجودی کیف پول",

    CardBankNumber: "شماره کارت",
    BankAccountNumber: "شماره حساب",
    IBanBankNumber: "شماره شبا",
};

const SECTIONS = [
    { title: "اطلاعات فردی", keys: ["Name", "LastName", "FatherName", "NationalCode", "BirthDate", "Job"] },
    { title: "اطلاعات تماس", keys: ["MobileNumber", "Phone", "City", "Town", "Address"] },
    { title: "حساب کاربری", keys: ["BussinessName", "UserName", "UserId", "Balance", "Roles", "GroupNames"] },
    { title: "اطلاعات بانکی", keys: ["CardBankNumber", "BankAccountNumber", "IBanBankNumber"] },
];

const EMPTY = "-";

function toList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
    } catch (e) {
        // رشته‌ی ساده‌ی جداشده با کاما
    }
    return trimmed.split(/[,،\n]/).map(item => item.trim()).filter(Boolean);
}

function TagList({ items, collapseAfter = 3 }) {
    const [expanded, setExpanded] = useState(false);
    const canCollapse = items.length > collapseAfter;
    const visibleItems = expanded || !canCollapse ? items : items.slice(0, collapseAfter);

    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visibleItems.map((item, index) => (
                <Tag
                    key={index}
                    style={{
                        margin: 0,
                        whiteSpace: "normal",
                        lineHeight: 1.7,
                        color: "var(--brand-primary, #1677ff)",
                        background: "var(--glass-brand, rgba(22,119,255,0.1))",
                        borderColor: "rgba(var(--brand-rgb, 22, 119, 255), 0.25)",
                    }}
                >
                    {item}
                </Tag>
            ))}
            {canCollapse && (
                <Button
                    type="link"
                    size="small"
                    onClick={() => setExpanded(prev => !prev)}
                    style={{ fontSize: 12, height: "auto", padding: 0, alignSelf: "center" }}
                >
                    <span style={{ textDecoration: "underline" }}>
                        {expanded ? "نمایش کمتر" : `نمایش بیشتر (${strings.getFarsiNum(String(items.length - collapseAfter))})`}
                    </span>
                </Button>
            )}
        </div>
    );
}

function renderValue(key, value) {
    if (value === null || value === undefined || value === "") {
        return <span style={{ color: "var(--text-faint, #bfbfbf)" }}>{EMPTY}</span>;
    }

    switch (key) {
        case "BirthDate": {
            const m = moment(value);
            return m.isValid()
                ? strings.getFarsiNum(m.format("jYYYY/jMM/jDD"))
                : strings.getFarsiNum(value);
        }
        case "Balance": {
            const num = Number(value);
            if (Number.isNaN(num)) return strings.getFarsiNum(value);
            return `${strings.getFarsiNum((num / 10).toLocaleString())} تومان`;
        }
        case "GroupNames":
        case "Roles": {
            const items = toList(value);
            if (!items.length) return <span style={{ color: "var(--text-faint, #bfbfbf)" }}>{EMPTY}</span>;
            return <TagList items={items} collapseAfter={8} />;
        }
        case "MobileNumber":
        case "Phone":
        case "NationalCode":
        case "CardBankNumber":
        case "BankAccountNumber":
        case "IBanBankNumber":
        case "UserId":
            return (
                <span style={{ direction: "ltr", display: "inline-block", unicodeBidi: "plaintext" }}>
                    {strings.getFarsiNum(String(value))}
                </span>
            );
        default:
            return typeof value === "object"
                ? JSON.stringify(value)
                : strings.getFarsiNum(String(value));
    }
}

function Field({ label, children, fullWidth = false }) {
    return (
        <div
            style={{
                background: "var(--glass-subtle, #fafafa)",
                border: "var(--glass-border-soft, 1px solid #f0f0f0)",
                borderRadius: 8,
                padding: "10px 14px",
                minWidth: 0,
                ...(fullWidth ? { gridColumn: "1 / -1" } : null),
            }}
        >
            <div style={{ fontSize: 12, color: "var(--text-muted, #8c8c8c)", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong, #262626)", wordBreak: "break-word" }}>
                {children}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 20 }}>
            <div
                style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--brand-primary, #1677ff)",
                    marginBottom: 10,
                    paddingRight: 8,
                    borderRight: "3px solid var(--brand-primary, #1677ff)",
                }}
            >
                {title}
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: 12,
                    alignItems: "start",
                }}
            >
                {children}
            </div>
        </div>
    );
}

export default function UserInformationTab({ userId, active = true }) {
    const reports = useReportsStore();
    const { data, loading, error } = reports.userInfo;

    useEffect(() => {
        if (active && userId) {
            reports.getUserInfo(userId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, active]);

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Spin tip="در حال دریافت اطلاعات کاربر..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 20 }}>
                <Alert type="error" showIcon message={error} />
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: 40 }}>
                <Empty description="اطلاعاتی برای نمایش وجود ندارد" />
            </div>
        );
    }

    const knownKeys = SECTIONS.flatMap(section => section.keys);
    const extraKeys = Object.keys(data).filter(key => !knownKeys.includes(key));

    return (
        <div style={{ padding: "16px 20px" }}>
            {SECTIONS.map(section => {
                const keys = section.keys.filter(key => key in data);
                if (!keys.length) return null;
                return (
                    <Section key={section.title} title={section.title}>
                        {keys.map(key => (
                            <Field
                                key={key}
                                label={FIELD_LABELS[key] || key}
                                fullWidth={key === "GroupNames" || key === "Roles" || key === "Address"}
                            >
                                {renderValue(key, data[key])}
                            </Field>
                        ))}
                    </Section>
                );
            })}

            {extraKeys.length > 0 && (
                <Section title="سایر اطلاعات">
                    {extraKeys.map(key => (
                        <Field key={key} label={key}>
                            {renderValue(key, data[key])}
                        </Field>
                    ))}
                </Section>
            )}
        </div>
    );
}
